<template>
  <!-- "Today from the Mystics" (mystic-page 2026-10-29, PNGs 07/08): the row
       between Your Mystics and Meet the Mystics, and (full) its See all feed.
       The row's compact cards: your-mystics-strip 2026-10-30 §4.
       Only drops that exist; nothing here ever makes one (API.md §5). -->
  <section v-if="shown.length" class="tfm" :class="{ full }" data-testid="today-from-mystics" aria-labelledby="tfm-h">
    <template v-if="full">
      <button type="button" class="tfm-back" data-testid="drops-back" @click="$emit('back')">‹ Mystics</button>
      <h2 id="tfm-h" class="tfm-title">Today from the Mystics</h2>
      <p class="tfm-date">{{ dateLine }}</p>
    </template>
    <template v-else>
      <div class="tfm-sec">
        <h3 id="tfm-h">Today from the Mystics</h3>
        <button type="button" class="tfm-all" data-testid="drops-see-all" @click="$emit('see-all')">See all ›</button>
      </div>
      <p class="tfm-why">{{ hidden ? 'Written this morning.' : 'Written this morning. Your Mystics first.' }}</p>
    </template>

    <!-- See all: the feed keeps its polaroids (the OPTIONAL trim is not built). -->
    <div v-if="full" class="tfm-grid">
      <article
        v-for="c in shown" :key="c.key" class="dp" data-testid="drop-polaroid"
        role="button" tabindex="0" :aria-label="`${c.title}, from ${c.name}`"
        @click="$emit('open-mystic', c.sub)" @keydown.enter="$emit('open-mystic', c.sub)"
      >
        <div class="dp-frame">
          <img v-if="c.image" class="dp-img" :src="c.image" :alt="c.title" loading="lazy" draggable="false" />
          <div v-else class="dp-img dp-noimg" aria-hidden="true">✦</div>
          <KeepButton :keep="c.keep" :kept="c.kept" />
        </div>
        <h4 class="dp-title">{{ c.title }}</h4>
        <p class="dp-line">{{ c.line }}</p>
        <div class="dp-mystic"><img v-if="c.portrait" :src="c.portrait" alt="" loading="lazy" draggable="false" /><span>{{ c.name }}</span></div>
      </article>
    </div>

    <!-- The row: slim cards, two rows sliding sideways from 4 (your-mystics-strip 2026-10-30 §4). -->
    <div v-else class="dr" :class="{ two: shown.length >= 4 }" data-testid="drops-row">
      <article
        v-for="c in shown" :key="c.key" class="dc" data-testid="drop-card" :data-sub="c.sub"
        role="button" tabindex="0" :aria-label="`${c.title}, from ${c.name}`"
        @click="$emit('open-mystic', c.sub)" @keydown.enter.self="$emit('open-mystic', c.sub)"
      >
        <div class="di">
          <img v-if="c.image" :src="c.image" :alt="c.title" loading="lazy" draggable="false" />
          <div v-else class="di-no" aria-hidden="true">✦</div>
        </div>
        <div class="dt">
          <div class="dtt">{{ c.title }}</div>
          <div class="dln">{{ c.line }}</div>
          <div class="dm">
            <img v-if="c.portrait" :src="c.portrait" alt="" loading="lazy" draggable="false" />
            <span>{{ c.name }}</span>
            <span v-if="isYours(c)" class="yours" aria-label="One of Your Mystics">★</span>
          </div>
        </div>
        <KeepButton compact :keep="c.keep" :kept="c.kept" @note="$emit('note', $event)" />
      </article>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { store } from '@/store'
import { getMysticDrops } from '@/api-service'
import { clientToday } from '@/utils/general-utils'
import { dropsDateLine, dropCards } from '@/utils/mystic-drops'
import KeepButton from '@/components/ui/KeepButton.vue'

const props = defineProps({
  full: { type: Boolean, default: false },
  // One Mystic: the home card already shows that Mystic's drop (§4).
  hideSub: { type: String, default: '' },
  // The tab's Your Mystics order, so the gold ★ follows a star made here.
  mine: { type: Array, default: null },
})
defineEmits(['open-mystic', 'see-all', 'back', 'note'])

const body = ref(null)
const cards = computed(() => dropCards(body.value))
const hidden = computed(() => !props.full && !!props.hideSub)
const shown = computed(() => hidden.value ? cards.value.filter((c) => c.sub !== props.hideSub) : cards.value)
const isYours = (c) => (Array.isArray(props.mine) ? props.mine.includes(c.sub) : c.yours)
const dateLine = computed(() => dropsDateLine((body.value && body.value.date) || clientToday()))

onMounted(async () => {
  try {
    body.value = await getMysticDrops(store.session_id, { limit: props.full ? 60 : 12, date: clientToday() })
  } catch (e) {
    body.value = null   // the tab stays as built
  }
})
</script>

<style scoped>
.tfm {
  --cream: #fff4dc; --dim: #8f7fb0; --lilac: #c3a9ff;
  --serif: 'Cormorant Garamond', Georgia, serif; --ui: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
  margin: 18px 0 4px;
}
.tfm button { font: inherit; background: none; border: 0; cursor: pointer; padding: 0; }
.tfm-sec { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin: 22px 0 4px; }
.tfm-sec h3 { margin: 0; font: 700 20px var(--serif); color: var(--cream); }
.tfm-all { font: 600 13px var(--ui) !important; color: var(--lilac) !important; }
.tfm-all:hover { color: #FFDFA3 !important; }
.tfm-why { margin: 0 0 12px; font: 500 12px/1.4 var(--ui); color: var(--dim); }
.tfm-back { font: 600 14px var(--ui) !important; color: var(--lilac) !important; margin: 14px 0 6px; }
.tfm-title { margin: 0; font: 700 26px/1.15 var(--serif); color: var(--cream); }
.tfm-date { margin: 4px 0 16px; font: 500 13px var(--ui); color: var(--dim); }

/* The row (your-mystics-strip 2026-10-30 §4, prototype "NEW"): slim cards,
   two rows from 4, sliding sideways. */
.dr { display: grid; grid-auto-flow: column; grid-template-rows: auto; grid-auto-columns: 84%; gap: 8px 10px; overflow-x: auto; scroll-snap-type: x proximity; scrollbar-width: none; margin-right: -16px; padding: 0 16px 4px 0; }
.dr.two { grid-template-rows: auto auto; }
.dr::-webkit-scrollbar { display: none; }
@media (min-width: 600px) { .dr { grid-auto-columns: calc((100% - 10px) / 2.15); } }
@media (min-width: 1000px) { .dr { grid-auto-columns: calc((100% - 20px) / 3.15); } }
@media (min-width: 761px) { .dr { margin-right: 0; padding-right: 0; } }
.dc { position: relative; display: grid; grid-template-columns: 60px minmax(0,1fr); gap: 11px; align-items: center; padding: 8px; border-radius: 13px; cursor: pointer; scroll-snap-align: start; min-width: 0;
  background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.08); transition: border-color .2s; }
.dc:hover, .dc:focus-visible { border-color: rgba(231,201,131,.4); outline: 0; }
.dc .di { width: 60px; height: 60px; border-radius: 9px; overflow: hidden; background: #0a0015; }
.dc .di img { display: block; width: 100%; height: 100%; object-fit: cover; }
.dc .di-no { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #c9a94e; font-size: 18px; }
.dc .dt { min-width: 0; }
.dc .dtt { font: 700 16px/1.15 var(--serif); color: var(--cream); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dc .dln { font: italic 500 13.5px/1.25 var(--serif); color: #cdbbe9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dc .dm { margin-top: 4px; display: flex; align-items: center; gap: 6px; min-width: 0; font: 500 11.5px var(--ui); color: #b9a6d6; padding-right: 66px; }
.dc .dm img { display: block; width: 16px; height: 16px; border-radius: 4px; object-fit: cover; flex: 0 0 auto; }
.dc .dm span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dc .dm .yours { color: #e7c983; flex: 0 0 auto; }
.dc :deep(.keep) { top: auto; right: 8px; bottom: 7px; }
.tfm-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
@media (min-width: 761px) { .tfm-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; } }

/* A polaroid: the image, the title, one short line, the Mystic's square. */
.dp {
  scroll-snap-align: start; min-width: 0;
  display: flex; flex-direction: column; gap: 4px;
  padding: 8px 8px 10px; border-radius: 14px;
  background: rgba(255, 255, 255, .03); border: 1px solid rgba(255, 255, 255, .08);
  cursor: pointer; transition: border-color .2s, transform .25s;
}
.dp:hover, .dp:focus-visible { border-color: rgba(231, 201, 131, .4); outline: 0; }
.dp-frame { position: relative; border-radius: 10px; overflow: hidden; aspect-ratio: 1 / 1; background: #0a0015; }
.dp-img { display: block; width: 100%; height: 100%; object-fit: cover; }
.dp-noimg { display: flex; align-items: center; justify-content: center; color: #c9a94e; font-size: 22px; }
.dp-frame :deep(.keep) { top: 6px; right: 6px; }
.dp-frame :deep(.kb) { font-size: 11.5px; padding: 5px 10px; }
.dp-title { margin: 6px 2px 0; font: 700 17px/1.15 var(--serif); color: var(--cream); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dp-line {
  margin: 0 2px; font: italic 500 14px/1.3 var(--serif); color: #cdbbe9;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.dp-mystic { display: flex; align-items: center; gap: 6px; margin: 4px 2px 0; font: 500 12.5px var(--ui); color: #b9a6d6; min-width: 0; }
.dp-mystic img { width: 20px; height: 20px; border-radius: 4px; object-fit: cover; flex: 0 0 auto; }
.dp-mystic span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
@media (prefers-reduced-motion: reduce) { .dp, .dc { transition: none; } }
</style>
